import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Pause, Play, SkipBack } from 'lucide-react';
import PreviewCanvas from '../../EffectBlocks/PreviewCanvas';
import { useCompositor } from './useCompositor';
import {
    formatSeconds,
    getLayerByKind,
    overlayPlacement,
    pipParams,
} from '../state/timelineModel';

/**
 * The stage: what the edit looks like right now.
 *
 * It is always on screen and always current. The player it replaces sat behind
 * a "Preview plan" button and warned that "fades, crops and other effects
 * appear only in the final render" — which meant the only way to see your own
 * edit was to pay for it. Effects here are drawn live over the frame, in the
 * same normalized coordinates the renderer resolves, so what you place is what
 * comes back.
 */

const RENDER_WIDTH = 1080;
const RENDER_HEIGHT = 1920;

function Stage({
    graph,
    playhead,
    isPlaying,
    totalDuration,
    selection,
    assetsByFileName,
    resolveAssetUrl,
    editable,
    onPlayheadChange,
    onPlayingChange,
    onSelect,
    onEffectChange,
}) {
    const canvasRef = useRef(null);

    const {
        audioRef, activeVideo, loading, error, frameSize, overlayClips, mainEnd, insetSizes,
    } = useCompositor({
        graph,
        playhead,
        isPlaying,
        totalDuration,
        assetsByFileName,
        resolveAssetUrl,
        canvasRef,
        onPlayheadChange,
        onPlayingChange,
    });

    /* The canvas backing store is the render resolution, not the display size,
       so a text block placed at a given fraction lands on the same pixel here
       as it does in the export. CSS scales it down to fit. */
    const aspectRatio = useMemo(() => {
        // The decoded frame first: it is measured, not recorded, so it is right
        // even when the asset record carries no dimensions at all -- which is the
        // common case, and which used to letterbox landscape footage into a
        // sliver of the portrait default.
        if (frameSize?.width && frameSize?.height) return frameSize.width / frameSize.height;
        const asset = assetsByFileName.get(String(activeVideo?.asset_filename || ''));
        const width = Number(asset?.Width) || RENDER_WIDTH;
        const height = Number(asset?.Height) || RENDER_HEIGHT;
        return width / height;
    }, [frameSize, activeVideo, assetsByFileName]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = RENDER_WIDTH;
        canvas.height = Math.round(RENDER_WIDTH / aspectRatio);
    }, [aspectRatio]);

    /* Only the effects that are live at this instant, and that belong to the
       clip under the playhead. Showing an effect outside its own span would
       misreport the edit as surely as not showing it at all. */
    const visibleEffects = useMemo(() => {
        const layer = getLayerByKind(graph, 'effects', 'layer 0');
        return (layer?.items || [])
            .filter((effect) => {
                if (!effect.type || effect.readOnly) return false;
                if (effect.clipId && effect.clipId !== activeVideo?.id) return false;
                const start = effect.start ?? 0;
                const end = effect.end ?? start;
                // A zero-length effect is a "from here on" marker, not an instant.
                if (Math.abs(end - start) < 1e-6) return playhead >= start;
                return playhead >= start && playhead < end;
            })
            .map((effect) => {
                const params = effect.params || {};
                if (effect.type !== 'fade') return { id: effect.id, type: effect.type, params };

                /* A fade is the one effect whose appearance depends on *when*
                   you are, not just what it is. The block layer draws in
                   normalized space and has no clock, so the ramp is resolved
                   here, where the playhead lives, and handed over as a plain
                   opacity. Out ramps to black, in ramps away from it. */
                const start = effect.start ?? 0;
                const end = effect.end ?? start;
                const span = Math.max(end - start, 1e-6);
                const progress = Math.min(1, Math.max(0, (playhead - start) / span));
                const opacity = params.fadeType === 'out' ? progress : 1 - progress;
                return { id: effect.id, type: effect.type, params: { ...params, opacity } };
            });
    }, [graph, activeVideo, playhead]);

    /* Flip and colour are not placeable blocks -- they change the picture
       itself, so they ride on the frame as a CSS transform/filter rather than as
       something drawn over it. Deliberately applied to the canvas only, not to
       the overlay blocks: the renderer flips the video and *then* draws text, so
       mirroring the titles here would misreport the cut.

       The colour maths approximates the ffmpeg grade rather than matching it --
       CSS has no white-balance control, so warmth is faked with sepia one way
       and a small hue rotation the other. The render stays authoritative. */
    const { frameTransform, frameFilter } = useMemo(() => {
        const transforms = [];
        const filters = [];

        visibleEffects.forEach(({ type, params }) => {
            if (type === 'flip') {
                transforms.push(params.direction === 'vertical' ? 'scaleY(-1)' : 'scaleX(-1)');
            }
            if (type === 'look') {
                const warmth = Number(params.warmth) || 0;
                const saturation = Number(params.saturation ?? 1);
                const brightness = Number(params.brightness) || 0;
                if (params.grayscale) filters.push('grayscale(1)');
                if (saturation !== 1) filters.push(`saturate(${saturation})`);
                if (brightness !== 0) filters.push(`brightness(${(1 + brightness).toFixed(3)})`);
                if (warmth !== 0) {
                    filters.push(warmth > 0
                        ? `sepia(${(warmth * 0.4).toFixed(3)})`
                        : `hue-rotate(${(warmth * 20).toFixed(1)}deg)`);
                }
            }
        });

        return {
            frameTransform: transforms.length ? transforms.join(' ') : undefined,
            frameFilter: filters.length ? filters.join(' ') : undefined,
        };
    }, [visibleEffects]);

    /* A handle over each inset on screen, so it is moved by dragging it where
       it is seen. The inset itself is painted on the canvas; this is only its
       outline, placed by the same arithmetic the paint loop and the render use,
       as fractions of the frame so it tracks the surface at any display size. */
    const insetBlocks = useMemo(() => {
        if (playhead >= mainEnd) return [];
        const frameWidth = RENDER_WIDTH;
        const frameHeight = Math.round(RENDER_WIDTH / aspectRatio);
        return overlayClips
            .filter(inset => playhead >= inset.start && playhead < inset.end)
            .map((inset) => {
                const size = insetSizes[String(inset.asset_filename || '')];
                if (!size) return null;
                const params = pipParams(inset);
                const rect = overlayPlacement(frameWidth, frameHeight, size.width, size.height,
                    params.x, params.y, params.scale);
                return {
                    id: inset.id,
                    type: 'pip',
                    params,
                    rect: {
                        left: rect.left / frameWidth,
                        top: rect.top / frameHeight,
                        width: rect.width / frameWidth,
                        height: rect.height / frameHeight,
                    },
                };
            })
            .filter(Boolean);
    }, [overlayClips, insetSizes, playhead, mainEnd, aspectRatio]);

    const insetIds = useMemo(() => new Set(overlayClips.map(inset => inset.id)), [overlayClips]);

    const handleBlockChange = useCallback((id, nextParams) => {
        // Fired continuously while dragging on the stage, so it coalesces into
        // a single undo entry rather than one per pointermove.
        onEffectChange?.(id, { params: nextParams }, true);
    }, [onEffectChange]);

    const stickerUrl = useMemo(() => {
        const selected = visibleEffects.find(effect => effect.id === selection?.id);
        return selected?.params?.imageUrl || selected?.params?.url || null;
    }, [visibleEffects, selection]);

    return (
        <div className="nle-stage">
            {/* The surface is size-contained, so CSS cannot learn the clip's
                shape from its contents -- it is handed down explicitly here. */}
            <div className="nle-stage-surface" style={{ '--nle-stage-aspect': aspectRatio }}>
                <PreviewCanvas
                    aspectRatio={aspectRatio}
                    blocks={editable ? [...insetBlocks, ...visibleEffects] : []}
                    selectedId={selection?.id}
                    stickerUrl={stickerUrl}
                    onChange={handleBlockChange}
                    onSelect={(id) => onSelect(id ? { kind: insetIds.has(id) ? 'clip' : 'effect', id } : null)}
                    frameTransform={frameTransform}
                    frameFilter={frameFilter}
                    renderFrame={(frameStyle) => (
                        <canvas ref={canvasRef} className="eb-frame nle-stage-canvas" style={frameStyle} />
                    )}
                />
                <audio ref={audioRef} preload="auto" />
            </div>

            <div className="nle-transport">
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onPlayingChange(!isPlaying)}
                    title={isPlaying ? 'Pause (space)' : 'Play (space)'}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => { onPlayingChange(false); onPlayheadChange(0); }}
                    title="Back to start"
                    aria-label="Back to start"
                >
                    <SkipBack size={16} aria-hidden="true" />
                </button>

                <span className="nle-transport-time">
                    {formatSeconds(playhead)} / {formatSeconds(totalDuration)}
                </span>

                {loading && <span className="nle-stage-note">Loading media…</span>}
                {error && <span className="nle-stage-error">{error}</span>}
            </div>
        </div>
    );
}

export default Stage;
